<template>
  <div class="card p-4 shadow-sm">
    <div class="mb-4">
      <h5 class="mb-1">Required Enrollment Documents</h5>
      <p class="text-muted small mb-0">Upload your own documents. Your employer can see completion status only, not the source files.</p>
    </div>

    <div v-if="!enrollmentId" class="alert alert-warning mb-0">Select an employer before uploading documents.</div>

    <div v-else class="row">
      <div v-for="doc in visibleDocuments" :key="doc.type" class="col-12 col-lg-6 mb-3">
        <div class="border rounded p-3 h-100 bg-white">
          <div class="d-flex justify-content-between align-items-start gap-3 mb-2">
            <div>
              <h6 class="mb-1"><i :class="doc.icon" class="me-2 text-success"></i>{{ doc.label }}</h6>
              <p class="small text-muted mb-0">{{ doc.description }}</p>
            </div>
            <span class="badge" :class="badgeClass(doc.type)">{{ statusLabel(doc.type) }}</span>
          </div>

          <div v-if="fileName(doc.type)" class="small text-secondary mb-2 text-truncate">
            Current file: {{ fileName(doc.type) }}
          </div>

          <div v-if="rejectionReason(doc.type)" class="alert alert-danger py-2 small">
            {{ rejectionReason(doc.type) }}
          </div>

          <div class="d-flex gap-2 align-items-center mt-3">
            <input
              :ref="`file-${doc.type}`"
              type="file"
              class="form-control form-control-sm"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              :disabled="uploading === doc.type"
              @change="selectFile($event, doc.type)"
            />
            <button
              type="button"
              class="btn btn-success btn-sm mb-0"
              :disabled="uploading === doc.type || !selectedFiles[doc.type]"
              @click="upload(doc.type)"
            >
              <span v-if="uploading === doc.type" class="spinner-border spinner-border-sm"></span>
              <span v-else>{{ isSubmitted(doc.type) ? 'Replace' : 'Upload' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'EnrollmentDocuments',
  props: {
    enrollmentId: { type: [Number, String], default: null },
    drivingRequired: { type: Boolean, default: false },
    enrollmentData: { type: Object, default: null },
  },
  emits: ['enrollment-updated'],
  data() {
    return {
      selectedFiles: {},
      uploading: null,
      definitions: [
        { type: 'id_copy', label: 'ID Copy', description: 'Government-issued photo identification.', icon: 'fas fa-id-card', drivingOnly: false },
        { type: 'social_security_card', label: 'Social Security Card', description: 'Social Security card or accepted equivalent record.', icon: 'fas fa-credit-card', drivingOnly: false },
        { type: 'live_scan', label: 'Live Scan Results', description: 'Background check or fingerprinting result.', icon: 'fas fa-fingerprint', drivingOnly: false },
        { type: 'govt_approval', label: 'Government Approval', description: 'Regional Center or agency approval documentation.', icon: 'fas fa-file-contract', drivingOnly: false },
        { type: 'identification_number_doc', label: 'Identification Number Document', description: 'Required identification or tax-number documentation.', icon: 'fas fa-file-alt', drivingOnly: false },
        { type: 'auto_insurance', label: 'Auto Insurance', description: 'Current auto insurance certificate when driving is required.', icon: 'fas fa-car', drivingOnly: true },
        { type: 'driver_license', label: "Driver's License", description: 'Valid driver license when driving is required.', icon: 'fas fa-id-badge', drivingOnly: true },
      ],
    };
  },
  computed: {
    visibleDocuments() {
      return this.definitions.filter((doc) => !doc.drivingOnly || this.drivingRequired);
    },
    documentMap() {
      const map = {};
      const docs = Array.isArray(this.enrollmentData?.documents) ? this.enrollmentData.documents : [];
      docs.forEach((doc) => { map[doc.document_type] = doc; });
      return map;
    },
  },
  methods: {
    selectFile(event, type) {
      const file = event.target.files?.[0] || null;
      if (file && file.size > 10 * 1024 * 1024) {
        Swal.fire('File Too Large', 'Maximum file size is 10 MB.', 'error');
        event.target.value = '';
        this.selectedFiles[type] = null;
        return;
      }
      this.selectedFiles = { ...this.selectedFiles, [type]: file };
    },
    async upload(type) {
      const file = this.selectedFiles[type];
      if (!file || !this.enrollmentId) return;
      this.uploading = type;
      try {
        const payload = new FormData();
        payload.append('document_type', type);
        payload.append('file', file);
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/employee-enrollment/${this.enrollmentId}/upload-document`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        this.selectedFiles = { ...this.selectedFiles, [type]: null };
        const input = this.$refs[`file-${type}`];
        const el = Array.isArray(input) ? input[0] : input;
        if (el) el.value = '';
        this.$emit('enrollment-updated');
        const extractionMessage = response.data?.data?.extraction_message;
        Swal.fire({
          icon: 'success',
          title: 'Uploaded',
          text: extractionMessage || 'Document uploaded successfully.',
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (error) {
        const data = error.response?.data;
        const validation = data?.errors ? Object.values(data.errors).flat().join('\n') : null;
        Swal.fire('Upload Failed', validation || data?.message || 'Could not upload the document.', 'error');
      } finally {
        this.uploading = null;
      }
    },
    record(type) { return this.documentMap[type] || null; },
    fileName(type) { return this.record(type)?.original_filename || ''; },
    rejectionReason(type) { return this.record(type)?.rejection_reason || ''; },
    isSubmitted(type) { return ['uploaded', 'approved'].includes(this.record(type)?.status); },
    statusLabel(type) {
      const status = this.record(type)?.status || 'pending_upload';
      return ({ pending_upload: 'Not Submitted', uploaded: 'Submitted', approved: 'Approved', rejected: 'Rejected' })[status] || status;
    },
    badgeClass(type) {
      const status = this.record(type)?.status || 'pending_upload';
      return {
        'bg-secondary': status === 'pending_upload',
        'bg-primary': status === 'uploaded',
        'bg-success': status === 'approved',
        'bg-danger': status === 'rejected',
      };
    },
  },
};
</script>
